import { Card, CardContent } from "@/components/ui/card";
import { MessageSquare, Clock, CheckCircle, AlertCircle, XCircle, Calendar, TrendingUp, Users } from "lucide-react";
import { Ticket } from "@/services/ticketService";
import { cn } from "@/lib/utils";

interface TicketStatsProps {
  tickets: Ticket[];
}

export function TicketStats({ tickets }: TicketStatsProps) {
  const stats = {
    total: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    in_progress: tickets.filter((t) => t.status === 'in_progress').length,
    waiting_customer: tickets.filter((t) => t.status === 'waiting_customer').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    closed: tickets.filter((t) => t.status === 'closed').length,
    unread: tickets.filter((t) => t.adminUnread).length,
    // Bugün oluşturulan ticketlar
    todayNew: tickets.filter((t) => {
      const today = new Date();
      const createdDate = new Date(t.createdAt);
      return createdDate.toDateString() === today.toDateString();
    }).length
  };

  const items = [
    {
      label: "Toplam Destek Talebi",
      value: stats.total,
      icon: MessageSquare,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      subtitle: "Sistemde kayıtlı",
      subtitleColor: "text-blue-600",
    },
    {
      label: "Açık Talepler",
      value: stats.open,
      icon: AlertCircle,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      subtitle: stats.total > 0 ? `%${Math.round((stats.open / stats.total) * 100)} açık` : "Açık talep yok",
      subtitleColor: "text-green-600",
      showPulse: stats.open > 0,
    },
    {
      label: "İşlemde",
      value: stats.in_progress,
      icon: Clock,
      color: "text-blue-600",
      bgColor: "bg-blue-500/10",
      subtitle: stats.total > 0 ? `%${Math.round((stats.in_progress / stats.total) * 100)} işlemde` : "İşlemde talep yok",
      subtitleColor: "text-blue-600",
    },
    {
      label: "Müşteri Bekleniyor",
      value: stats.waiting_customer,
      icon: Users,
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
      subtitle: stats.total > 0 ? `%${Math.round((stats.waiting_customer / stats.total) * 100)} bekliyor` : "Bekleyen yok",
      subtitleColor: "text-yellow-600",
    },
    {
      label: "Çözüldü",
      value: stats.resolved,
      icon: CheckCircle,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      subtitle: stats.total > 0 ? `%${Math.round((stats.resolved / stats.total) * 100)} çözüldü` : "Çözülen yok",
      subtitleColor: "text-purple-600",
    },
    {
      label: "Kapatıldı",
      value: stats.closed,
      icon: XCircle,
      color: "text-gray-500",
      bgColor: "bg-gray-500/10",
      subtitle: stats.total > 0 ? `%${Math.round((stats.closed / stats.total) * 100)} kapalı` : "Kapalı yok",
      subtitleColor: "text-gray-600",
    },
    {
      label: "Bugün Oluşturulan",
      value: stats.todayNew,
      icon: Calendar,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      subtitle: stats.todayNew > 0 ? "Yeni talepler var" : "Yeni talep yok",
      subtitleColor: "text-orange-600",
      showPulse: stats.todayNew > 0,
    },
    {
      label: "Okunmamış Mesajlar",
      value: stats.unread,
      icon: TrendingUp,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      subtitle: stats.unread > 0 ? "Acil kontrol gerekli" : "Okunmamış yok",
      subtitleColor: "text-red-600",
      showPulse: stats.unread > 0,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((item) => (
        <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
          <CardContent className="p-0">
            <div className="relative">
              {/* Arka plan dekoratif gradient */}
              <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>

              <div className="p-6 relative">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                    <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                  </div>
                  <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                    <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                  </div>
                </div>

                {/* Alt bilgi çubuğu */}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-slate-600">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.showPulse ? `${item.color.replace('text-', 'bg-')} animate-pulse` : "bg-slate-300"}`}></span>
                      <span className="font-medium">{item.subtitle}</span>
                    </div>
                    {item.value > 0 && (
                      <span className={`text-xs font-semibold ${item.subtitleColor}`}>{item.value}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
